import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, MessageCircle, HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: "¿Necesito conocimientos previos para entrar?",
    answer: "Para las rutas de 'Fundamentos' partimos desde cero absoluto. Sin embargo, para las rutas 'Pro' o 'Full Stack', recomendamos tener nociones básicas de lógica de programación. Cada curso indica su nivel requerido."
  },
  {
    question: "¿El pago es una suscripción o pago único?",
    answer: "Tú tienes el control. Ofrecemos una suscripción mensual flexible (cancela cuando quieras) y un plan anual con un descuento del 40%. También puedes comprar cursos individuales de por vida si prefieres no suscribirte."
  },
  {
    question: "¿Los certificados tienen valor curricular?",
    answer: "Sí. Nuestros certificados son verificables mediante blockchain y tienen un ID único. Empresas de tecnología reconocen Zento como una fuente de talento confiable. Puedes añadirlos directamente a tu perfil de LinkedIn."
  },
  {
    question: "¿Qué pasa si me atasco en un ejercicio?",
    answer: "Nunca estarás solo. Tienes acceso a nuestro servidor de Discord privado donde mentores y otros estudiantes te ayudarán en tiempo real. En el plan 'Teams', tienes soporte prioritario directo con los instructores."
  },
  {
    question: "¿Puedo descargar los videos?",
    answer: "Sí, en los planes 'Pro' y 'Teams' puedes descargar las clases desde la app móvil para aprender offline cuando viajes o no tengas conexión."
  },
  {
    question: "¿Tienen garantía de devolución?",
    answer: "Absolutamente. Si en los primeros 14 días sientes que Zento no es para ti, te devolvemos el 100% de tu dinero. Sin preguntas incómodas."
  }
];

const FAQItem = ({ faq, isOpen, onClick }) => {
  return (
    <motion.div 
      initial={false}
      className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
        isOpen 
          ? 'bg-white dark:bg-[#111] border-blue-500/50 shadow-lg shadow-blue-500/10' 
          : 'bg-slate-50 dark:bg-[#0a0a0a] border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10'
      }`}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-6 text-left cursor-pointer focus:outline-none"
      >
        <span className={`text-lg font-bold transition-colors duration-300 ${isOpen ? 'text-blue-600 dark:text-blue-400' : 'text-slate-900 dark:text-white'}`}>
          {faq.question}
        </span>
        <div className={`p-2 rounded-full transition-colors duration-300 ${isOpen ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400' : 'bg-slate-200 dark:bg-white/10 text-slate-500'}`}>
          {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-6 pb-6 pt-0 text-slate-600 dark:text-slate-400 leading-relaxed">
              {faq.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0); 

  return (
    <section className="py-24 px-6 bg-white dark:bg-[#0a0a0a] transition-colors duration-500 relative">
      <div className="max-w-4xl mx-auto">
        
       
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 text-xs font-bold uppercase tracking-wider mb-6 border border-slate-200 dark:border-white/10">
            <HelpCircle className="w-3 h-3" />
            Resolvemos tus dudas
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6">
            Preguntas <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-500">Frecuentes</span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Todo lo que necesitas saber sobre la plataforma, facturación y metodología.
          </p>
        </div>

        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem 
              key={index} 
              faq={faq} 
              isOpen={openIndex === index} 
              onClick={() => setOpenIndex(openIndex === index ? null : index)} 
            />
          ))}
        </div>

        
        <div className="mt-16 p-8 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 dark:from-[#111] dark:to-[#050505] border border-slate-200 dark:border-white/10 text-center relative overflow-hidden group">
          
          
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] group-hover:bg-blue-500/20 transition-all duration-500"></div>
          
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 text-white border border-white/10">
              <MessageCircle className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">¿Aún tienes preguntas?</h3>
            <p className="text-slate-400 mb-8 max-w-md">
              Si no encuentras lo que buscas, nuestro equipo de soporte (humanos, no bots) está listo para ayudarte.
            </p>
            <div className="flex gap-4">
               <button className="px-6 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-100 transition-colors">
                 Chatear con Soporte
               </button>
               <button className="px-6 py-3 bg-transparent border border-white/20 text-white font-bold rounded-xl hover:bg-white/10 transition-colors">
                 Ir a Discord
               </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default FAQ;